// BrimLight/client/ui/CharacterEditor.js
import { ComponentBase } from "../core/ComponentBase.js";
import { MediaSelector } from "./MediaSelector.js"; // Import MediaSelector

export class CharacterEditor extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
    }

    async initialize() {
        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            instanceId: `${this.config.instanceId}-crud`,
            rpcClass: "CharacterRpc",
            schemaPath: "collections/characters",
            title: "Character Library",
            height: "auto", // Correct height for tabbed interface
            viewConfig: {
                mode: 'grid',
                fields: [
                    {
                        name: 'representativeImageRelativePath',
                        label: 'Avatar',
                        render: (val, item) => {
                            const src = val ? `/${val}?v=${item.assetHash || Date.now()}` : '';
                            return src ? `<img src="${src}" class="img-thumbnail" style="width: 50px; height: 50px; object-fit: cover; border-radius: 50%;">` : '<i class="bi bi-person-square" style="font-size: 2rem; color: var(--bs-secondary-color);"></i>';
                        }
                    },
                    { name: 'name', label: 'Name' },
                    { name: 'description', label: 'Description' },
                    { name: 'voice', label: 'Voice' },

                ],
                actions: [
                    {
                        label: 'Generate Image',
                        icon: 'bi-stars',
                        eventName: 'generateAsset',
                        visibleWhen: (item) => !item.representativeImageRelativePath || item.isDirty
                    },
                    {
                        label: 'Regenerate',
                        icon: 'bi-arrow-repeat',
                        eventName: 'regenerateAsset',
                        className: 'btn-outline-warning',
                        visibleWhen: (item) => item.representativeImageRelativePath && !item.isDirty
                    }
                ]
            },
            formConfig: {
                fields: {
                    name: { type: 'text', label: 'Character Name', required: true },
                    description: { type: 'textarea', label: 'Character Description', rows: 3 },
                    personalityTraits: { type: 'TagInput', label: 'Personality Traits', creatable: true, placeholder: "e.g., brave, witty, curious", component: "TagInput" },
                    visualAppearancePrompt: { type: 'textarea', label: 'Visual Appearance Prompt', rows: 5, helpText: "Describe the character's appearance for the image generation AI." },
                    representativeImageRelativePath: {
                        type: 'MediaSelector',
                        label: 'Representative Image (9:16)',
                        component: MediaSelector,
                        category: 'image',
                        maxFiles: 1
                    },
                    voice: {
                        type: 'VoiceSelector',
                        label: 'Character Voice',
                        component: "VoiceSelector",
                    },
                    musicThemeRelativePath: {
                        type: 'MediaSelector',
                        label: 'Music Theme',
                        component: MediaSelector,
                        category: 'audio',
                        maxFiles: 1
                    },
                },
                sections: [
                    { title: "Core Details", fields: ["name", "description", "personalityTraits"] },
                    {
                        title: "AI Generation & Assets",
                        fields: [
                            "visualAppearancePrompt",
                            "representativeImageRelativePath",
                            "voice",
                            "musicThemeRelativePath"
                        ]
                    }
                ]
            },
            dataConfig: {
                autoLoad: false
            }
        });

        const handleGeneration = async (item, force = false) => {
            const projectId = this.appClient.getSharedData('selectedProjectId');
            if (!projectId) {
                this.appClient.notifier.warn("Please select a project before generating assets.");
                return;
            }
            this.appClient.notifier.info(`Generating image for ${item.name}...`);
            try {
                await this.appClient.rpcClient.call('AssetGenerationRpc.generateCharacterAsset', {
                    characterId: item._id,
                    forceRegeneration: force
                }, { projectId }); // Pass projectId in context
                this.appClient.notifier.success(`Image for ${item.name} generated successfully.`);
                this.dataCrud.refreshData();
            } catch (error) {
                this.appClient.notifier.error(`Failed to generate image: ${error.message}`);
            }
        };
        this.dataCrud.dataView.on('generateAsset', (item) => handleGeneration(item, false));
        this.dataCrud.dataView.on('regenerateAsset', (item) => handleGeneration(item, true));

        await super.initialize();
    }

    async initAfterLogin() {
        if (this.dataCrud) {
            await this.dataCrud.refreshData();
        }
    }

    async render() {
        return this.dataCrud.render();
    }
}